/**
 * Source data for the "What cited dental practices do differently" deep-dive
 * (HAL-188) – the cited-vs-uncited case-control that is the spine of Dental
 * Economics Part 2.
 *
 * Same June–July 2026 dental corpus as `/research/chatgpt-dental-citations`
 * (HAL-187): the 271 audited cited sites are the most-cited practice-owned
 * domains in that corpus; the 269 matched uncited sites are NPPES dental
 * organisations matched on metro × area of dentistry and never cited in it.
 * Every figure reproduces from
 * `data/research/mgma-2026/dental-analysis/dental-case-control.json` (with the
 * third-party split from `control-webpresence.json` and the within-cited
 * correlation from `dental-depth-correlation.json`). That tree is gitignored,
 * so `data.spec.ts` pins these constants with hardcoded corpus-verified values
 * and cross-checks the live JSON where it exists locally.
 *
 * Two numbers here are easy to conflate and the page keeps them apart:
 *
 * - The long-content threshold is 1,000 words, not the 500 the analysis script
 *   labelled it. `scripts/research/mgma-2026/article1/lib/lever-classifier.ts`
 *   scores L3 at ≥ 1,000 words of `<main>` text with nav/header/footer
 *   stripped, and no L3-positive row in the audit CSV falls under 1,000.
 * - `word_count_max` (the 1,184 / 905 medians) is a different measure: every
 *   word on the longest fetched page with only scripts and styles removed
 *   (`extended-signals.ts`), navigation and footer included. That is why a
 *   1,184-word median coexists with only 33.6% of sites clearing the 1,000-word
 *   classifier – the two counts are not on one scale, and the table says so.
 *
 * Effect sizes are Cochran–Mantel–Haenszel odds ratios stratified on the
 * matching variables. An odds ratio of 2.14 is not "twice as likely": the
 * relative prevalence is 33.6 / 19.3 ≈ 1.7, which is what the prose says.
 */

export const N_CITED = 271;
export const N_UNCITED = 269;
export const N_COMPARED = N_CITED + N_UNCITED;
/** Uncited practices drawn before website resolution and crawl attrition. */
export const N_UNCITED_DRAWN = 347;
/** Cited domains crawled (the most-cited practice-owned domains); 271 returned an audit. */
export const N_CITED_CRAWLED = 300;
export const METRO_COUNT = 20;
export const AREA_COUNT = 7;

/** The L3 classifier threshold, in words of body text (see the header). */
export const LONG_CONTENT_WORDS = 1000;

/** Relative prevalence of long content, cited over uncited, one decimal. */
export const LONG_CONTENT_RELATIVE_PREVALENCE = 1.7;

/** A label/value pair rendered in the hero stat strip (`<dl>`). */
export interface HeroStat {
  readonly label: string;
  readonly value: string;
  /** `figure` renders in the display face; `text` in the body face. */
  readonly kind: 'figure' | 'text';
}

/** Hero stat strip shown under the lede, in left-to-right render order. */
export const HERO_STATS: readonly HeroStat[] = [
  { label: 'Cited sites', value: '271', kind: 'figure' },
  { label: 'Matched uncited', value: '269', kind: 'figure' },
  { label: 'Matched on', value: '20 metros × 7 areas of dentistry', kind: 'text' },
  { label: 'Audited', value: 'July 2026', kind: 'text' },
];

/* ============ The five on-site features + the composite ============ */

export interface FeatureRow {
  /** Stable identifier matching the JSON's `per_feature` key. */
  readonly key: string;
  readonly label: string;
  /** One-line operational definition shown under the label. */
  readonly definition: string;
  /** Share of cited sites carrying the feature (one decimal). */
  readonly citedPct: number;
  /** Share of matched uncited sites carrying the feature (one decimal). */
  readonly uncitedPct: number;
  /** Cochran–Mantel–Haenszel pooled odds ratio; null where too rare to pool. */
  readonly oddsRatio: number | null;
  /** CMH p-value, four decimals as the JSON carries it. */
  readonly p: number;
  /** Risk difference in percentage points, one decimal. */
  readonly riskDiffPp: number;
  /** Strata with sites in both groups that the CMH test pooled. */
  readonly informativeStrata: number;
  /** True where the matched test reached p < .05 – the amber cited bar. */
  readonly significant: boolean;
  /** The test line rendered under the two values. */
  readonly stat: string;
  /** The "Any of the five" row, rendered after a hairline. */
  readonly composite?: true;
}

/**
 * Presentation order: long content first (the spine), the four features whose
 * difference did not survive matching, then the composite after a hairline.
 * Putting the composite first would make the takeaway "have features" when
 * the data says "have depth".
 */
export const FEATURES: readonly FeatureRow[] = [
  {
    key: 'L3_long_content',
    label: 'Long content',
    definition:
      'At least one audited page of 1,000 words or more; treatment/condition page or homepage fallback',
    citedPct: 33.6,
    uncitedPct: 19.3,
    oddsRatio: 2.14,
    p: 0.0002,
    riskDiffPp: 14.2,
    informativeStrata: 60,
    significant: true,
    stat: 'OR 2.14 · p = .0002 · +14.2 pp',
  },
  {
    key: 'L2_faqpage_schema',
    label: 'FAQPage schema',
    definition: 'FAQPage markup carrying five or more patient-language questions with answers',
    citedPct: 8.9,
    uncitedPct: 6.7,
    oddsRatio: 1.46,
    p: 0.3319,
    riskDiffPp: 2.2,
    informativeStrata: 30,
    significant: false,
    stat: 'OR 1.46 · p = .33 · not significant',
  },
  {
    key: 'L4_onpage_qa_blocks',
    label: 'On-page Q&A blocks',
    definition: 'Three or more visible question-and-answer pairs or accordion panels',
    citedPct: 6.6,
    uncitedPct: 5.6,
    oddsRatio: 1.35,
    p: 0.5518,
    riskDiffPp: 1.1,
    informativeStrata: 20,
    significant: false,
    stat: 'OR 1.35 · p = .55 · not significant',
  },
  {
    key: 'L5_hero_intro_block',
    label: 'Structured intro block',
    definition: 'Opening text naming a credential, a numeric metric and a distinguishing phrase',
    citedPct: 3.0,
    uncitedPct: 0.7,
    oddsRatio: 4.82,
    p: 0.0795,
    riskDiffPp: 2.2,
    informativeStrata: 9,
    significant: false,
    stat: 'OR 4.82 · p = .08 · not significant',
  },
  {
    key: 'L1_physician_credential',
    label: 'Dentist credential schema',
    definition:
      'Person or Physician markup carrying a board certification and the body that issued it',
    citedPct: 0.4,
    uncitedPct: 0.0,
    oddsRatio: null,
    p: 0.9704,
    riskDiffPp: 0.4,
    informativeStrata: 1,
    significant: false,
    stat: 'Too rare to estimate · 1 site vs 0',
  },
  {
    key: 'ANY',
    label: 'Any of the five',
    definition: 'Carries at least one of the features above',
    citedPct: 38.7,
    uncitedPct: 26.0,
    oddsRatio: 1.94,
    p: 0.0008,
    riskDiffPp: 12.7,
    informativeStrata: 62,
    significant: true,
    stat: 'OR 1.94 · p = .0008 · +12.7 pp',
    composite: true,
  },
];

/** Shared bar scale (0–40%); no row exceeds it. */
export const FEATURE_SCALE_MAX_PCT = 40;

/** 95% CI of the risk difference, percentage points, for the two significant rows. */
export const RISK_DIFF_CI_PP = {
  L3_long_content: [6.9, 21.6],
  ANY: [4.9, 20.5],
} as const;

/** Share of sites in each group carrying none of the five features. */
export const NONE_OF_FIVE = { citedPct: 61.3, uncitedPct: 74.0 } as const;

/** FAQPage schema adopters behind the 8.9% / 6.7% row (the counts the prose quotes). */
export const FAQPAGE_ADOPTERS = { cited: 24, uncited: 18 } as const;

/**
 * Width of a bar as a percentage of the shared track, to two decimals with
 * trailing zeros dropped (`84%`, `48.25%`), which is also the form the DOM
 * hands back from `style.width`.
 */
export function barWidthPct(valuePct: number, max = FEATURE_SCALE_MAX_PCT): string {
  return `${Number(((valuePct / max) * 100).toFixed(2))}%`;
}

/* ============ Continuous measures (median per site) ============ */

export interface ContinuousRow {
  /** Stable identifier matching the JSON's `continuous` key. */
  readonly key: string;
  readonly label: string;
  /** What the measure actually counts – the row's fine print. */
  readonly definition: string;
  readonly citedMedian: number;
  readonly uncitedMedian: number;
  /** Rendered values (thousands separators where they apply). */
  readonly citedDisplay: string;
  readonly uncitedDisplay: string;
  /** The test line, e.g. "Mann–Whitney U · p < .0001". */
  readonly test: string;
}

export const CONTINUOUS: readonly ContinuousRow[] = [
  {
    key: 'word_count_max',
    label: 'Words on the longest fetched page',
    definition:
      'All page text with scripts and styles removed, navigation and footer included. A looser count than the 1,000-word classifier, which reads body content only, so the two are not on one scale',
    citedMedian: 1184,
    uncitedMedian: 905,
    citedDisplay: '1,184',
    uncitedDisplay: '905',
    test: 'Mann–Whitney U · p < .0001',
  },
  {
    key: 'schema_type_variety',
    label: 'Distinct schema.org types',
    definition: 'Distinct types used in the site’s schema.org\u00a0markup',
    citedMedian: 4,
    uncitedMedian: 2,
    citedDisplay: '4',
    uncitedDisplay: '2',
    test: 'Mann–Whitney U · p = .0001',
  },
  {
    key: 'entity_pages_found',
    label: 'Treatment or condition pages found',
    definition: 'Pages the crawl identified as describing a specific service or\u00a0condition',
    citedMedian: 0,
    uncitedMedian: 0,
    citedDisplay: '0',
    uncitedDisplay: '0',
    test: 'Mann–Whitney U · p = .52 · not significant',
  },
];

/**
 * Within the 271 cited sites, neither word count nor feature count tracked
 * citation frequency – but the sample is the most-cited domains, so the range
 * is restricted by construction and the null says little either way.
 */
export const WITHIN_CITED_SPEARMAN_ABS_MAX = 0.03;

/* ============ Website availability among the uncited draw ============ */

export interface WebPresenceSegment {
  readonly key: 'own' | 'third-party' | 'none';
  readonly label: string;
  readonly count: number;
  readonly pct: number;
  readonly note: string;
}

/** The three segments of the stacked bar, in render order (own site first). */
export const WEB_PRESENCE: readonly WebPresenceSegment[] = [
  {
    key: 'own',
    label: 'Own website',
    count: 292,
    pct: 84.1,
    note: '292 practices with an independent site; 269 of them returned a usable audit and form the comparison\u00a0group',
  },
  {
    key: 'third-party',
    label: 'Third-party page\u00a0only',
    count: 16,
    pct: 4.6,
    note: '16 practices: 12 on a DSO or group site, 4 on an association\u00a0listing',
  },
  {
    key: 'none',
    label: 'No findable website',
    count: 39,
    pct: 11.2,
    note: '39 practices with no site Google Places could resolve',
  },
];

/** Third-party-only + no website: the share with no independent site of their own. */
export const NO_INDEPENDENT_SITE_PCT = 15.8;
/** How the third-party-only 16 split (`control-webpresence.json`). */
export const THIRD_PARTY_SPLIT = { dsoGroup: 12, association: 4 } as const;

/* ============ How the two groups were built ============ */

export interface GroupColumn {
  readonly heading: string;
  readonly n: number;
  readonly rows: readonly (readonly [term: string, description: string])[];
}

export const GROUPS: readonly GroupColumn[] = [
  {
    heading: 'Cited group',
    n: N_CITED,
    rows: [
      [
        'Source',
        'The 300 practice-owned domains cited most often across the 13,152 citation events in the dental corpus',
      ],
      ['Selection', 'Independent practices only; hospital, DSO and directory domains excluded'],
      ['Audited', '271 of 300 returned a usable crawl; the rest blocked or failed'],
      ['Website', 'Live by construction'],
    ],
  },
  {
    heading: 'Comparison group',
    n: N_UNCITED,
    rows: [
      [
        'Source',
        'Dental organizations in the National Plan and Provider Enumeration System (NPPES), entity type 2',
      ],
      [
        'Selection',
        'Matched to the cited group on metro × area of dentistry; any domain appearing anywhere in the citation corpus excluded',
      ],
      [
        'Audited',
        '347 drawn; 292 with an independent website, found through Google Places; 269 returned a usable crawl',
      ],
      ['Website', 'Google Places lookup; see website availability'],
    ],
  },
];

/* ============ The four checks a reader can run on one page ============ */

/**
 * The first three are patient-communication checks, not signals this study
 * tested; the fourth is one live query, not the corpus. The page says so.
 */
export const AUDIT_CHECKS: readonly string[] = [
  'Does it answer the question patients ask before booking, in ordinary page text rather than an accordion, PDF or image?',
  'How many words does it carry? The study’s threshold was 1,000 on at least one page; two thirds of cited sites did not meet it.',
  'Is there a named clinician who reviewed it, and a date that reflects a real review?',
  'With web search on, ask ChatGPT “who does [treatment] in [city]?” Does this page appear among the sources?',
];

/* ============ FAQ (rendered visibly AND emitted as FAQPage JSON-LD) ============ */

export interface CaseControlFaq {
  readonly question: string;
  readonly answer: string;
}

/** FAQ pairs – the visible accordion and the FAQPage schema must match. */
export const FAQS: readonly CaseControlFaq[] = [
  {
    question: 'Does this mean every page needs 1,000 words?',
    answer:
      'No. One third of cited sites met the threshold on at least one page and one fifth of uncited sites did; two thirds of cited practices did not meet it. The study did not test the effect of adding words.',
  },
  {
    question: 'Is FAQ schema a waste of time?',
    answer:
      'The study cannot say that. FAQPage markup was slightly more common on cited sites (8.9% versus 6.7%) and the difference was not significant with 24 cited adopters. The audit checked whether the markup was present, not whether it accurately described the page.',
  },
  {
    question: 'Why match on market and specialty but not practice size or age?',
    answer:
      'Metro and area of dentistry are observable for every practice in NPPES; size and age are not. Matching makes the groups comparable by metro and area of dentistry. Differences in practice size or resources could still explain the association.',
  },
  {
    question: 'What does “uncited” mean here?',
    answer:
      'Absent from the 13,152 citation events in our corpus: 1,405 dental questions across 20 metros and seven areas of dentistry. A practice ChatGPT cites on questions we did not ask, or in a metro we did not sample, would count as uncited.',
  },
  {
    question: 'Does more content earn more citations once a practice is already cited?',
    answer:
      'Unknown. Within the cited group neither word count nor feature count tracked citation frequency, but the group is the 300 most-cited domains, so the range is restricted and the test is uninformative. The finding is about the difference between absent and cited practices, not about ranking among the cited.',
  },
];

/* ============ Dataset descriptor (JSON-LD) ============ */

/**
 * The case-control's own `Dataset` descriptor. It is a distinct view from both
 * the medical corpus and the Part 1 dental citation dataset (a matched audit of
 * 540 websites, not a citation pool), so the page passes its own schema.org
 * `@id` and technical fields.
 */
export const CASE_CONTROL_DATASET = {
  name: 'Halcy Dental Cited-vs-Uncited Website Case-Control',
  description:
    'A matched comparison of 271 dental practice websites ChatGPT (GPT-5.5) cited across 1,405 patient-realistic dental questions against 269 matched practices absent from the answers, audited for five on-site features, page length and schema.org markup. Matched on metro × area of dentistry; audited July 2026.',
  keywords: [
    'answer engine optimization',
    'AEO',
    'ChatGPT citations',
    'AI search visibility',
    'dental SEO',
    'case-control study',
    'dental practice websites',
  ],
  variableMeasured: [
    'on-site feature prevalence, cited versus matched uncited',
    'Cochran–Mantel–Haenszel odds ratio by feature',
    'median words on the longest fetched page',
    'median distinct schema.org types',
    'website availability among uncited practices',
  ],
  measurementTechnique:
    'Case-control audit: the most-cited practice-owned domains in the dental citation corpus against NPPES dental organisations matched on metro × area of dentistry, crawled with one classifier set; Cochran–Mantel–Haenszel tests stratified on the matching variables and Mann–Whitney U for continuous measures',
  temporalCoverage: '2026-06/2026-07',
  datePublished: '2026-09-17',
} as const;
